<template>
  <div
    class="service-record-list-item"
    :class="{
      'service-record-list-item--expanded': isExpanded,
    }"
  >
    <Toast />
    <div class="service-record-list-item__header">
      <div class="service-record-list-item__content" @click="handleItemClick">
        <div class="service-record-list-item__icon">
          <i class="pi pi-file" style="font-size: 1.5rem" />
        </div>
        <div class="service-record-list-item__text">
          <div
            v-if="record.nextServiceInfo"
            class="service-record-list-item__tertiary"
          >
            {{ record.nextServiceInfo }}
          </div>
          <div class="service-record-list-item__title">
            {{ record.title }}
          </div>
          <div class="service-record-list-item__subtitle">
            {{ record.subtitle }}
          </div>
        </div>
      </div>

      <div class="service-record-list-item__actions">
        <Tag
          :value="record.statusText"
          :icon="record.status === 'Completed' ? 'pi pi-check' : 'pi pi-clock'"
          :class="[
            'service-record-list-item__status-tag',
            {
              'service-record-list-item__status-tag--completed':
                record.status === 'Completed',
              'service-record-list-item__status-tag--pending':
                record.status === 'Pending',
            },
          ]"
        />
        <BaseButton
          v-show="false"
          v-if="isExpanded && record.status === 'Pending'"
          variant="tertiary"
          shape="rectangle"
          size="xs"
          text="Rediger"
          :icon="'pi pi-pencil'"
          :icon-alt="true"
          :uppercase="true"
          class="service-record-list-item__edit-button"
          title="Rediger serviceoppføring"
          @click="handleEditClick"
        />
        <BaseButton
          v-if="isExpanded && record.status === 'Pending'"
          variant="tertiary"
          shape="rectangle"
          size="xs"
          text="Slett"
          :icon="'pi pi-trash'"
          :icon-alt="true"
          :uppercase="true"
          class="service-record-list-item__delete-button"
          title="Slett serviceoppføring"
          @click="handleDeleteClick"
        />
        <div
          class="service-record-list-item__expand-icon"
          @click="handleItemClick"
        >
          <i
            :class="isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
            alt="Toggle details"
          />
        </div>
      </div>
    </div>

    <div v-if="isExpanded" class="service-record-list-item__collapse">
      <slot name="collapse-content" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Toast from "primevue/toast";
import Tag from "primevue/tag";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import type { ServiceRecordListItem } from "@/types/service-records";

interface Props {
  record: ServiceRecordListItem;
  isExpanded: boolean;
}

interface Emits {
  (e: "item-clicked", recordId: string): void;
  (e: "delete-clicked", recordId: string): void;
  (e: "edit-clicked", recordId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Event handlers
const handleItemClick = () => {
  emit("item-clicked", props.record.id);
};

const handleEditClick = () => {
  emit("edit-clicked", props.record.id);
};

const handleDeleteClick = () => {
  emit("delete-clicked", props.record.id);
};
</script>

<style lang="scss" scoped>
.service-record-list-item {
  background-color: $color-white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid $color-border-light;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }

  &--expanded {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    min-height: 80px;

    @media (max-width: $bp-sm) {
      padding: 1rem;
      min-height: 70px;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    cursor: pointer;
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 24px;
      height: 24px;
      opacity: 0.7;
    }

    @media (max-width: $bp-sm) {
      width: 32px;
      height: 32px;

      img {
        width: 20px;
        height: 20px;
      }
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
  }

  &__status {
    margin-bottom: 0.5rem;
  }

  &__status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;

    &--completed {
      background-color: $color-success-light;
      color: $color-success-dark;
    }

    &--pending {
      background-color: $color-warning-light;
      color: $color-warning-dark;
    }
  }

  &__tertiary {
    font-size: 0.875rem;
    color: $color-text-secondary;
    margin-bottom: 0.25rem;
  }

  &__title {
    font-weight: 600;
    color: $color-primary-darker;
    margin-bottom: 0.25rem;
    line-height: 1.3;
    text-transform: uppercase;

    @media (max-width: $bp-sm) {
      font-size: 0.9375rem;
    }
  }

  &__subtitle {
    color: $color-text-secondary;
    font-size: 0.875rem;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    width: 28rem;
    -webkit-box-orient: vertical;

    @media (max-width: $bp-sm) {
      font-size: 0.8125rem;
      -webkit-line-clamp: 1;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;

    @media (max-width: $bp-sm) {
      gap: 0.25rem;
    }
  }

  &__edit-button,
  &__delete-button {
    @media (max-width: $bp-sm) {
      font-size: 0.75rem;
      padding: 0.375rem 0.5rem;
    }
  }

  &__expand-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $color-primary-lighter;
    }

    img {
      width: 16px;
      height: 16px;
      transition: transform 0.2s ease;
    }
  }

  &__collapse {
    border-top: 1px solid $color-border-light;
    animation: expandCollapse 0.2s ease-out;
  }

  &__widgets {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-right: 1rem;
  }

  &__action-buttons {
    display: flex;
    gap: 0.5rem;
  }

  &__status-tag {
    &--completed {
      background-color: $color-success-light !important;
      color: $color-success-dark !important;
      border-color: $color-success !important;

      :deep(.p-tag-icon) {
        color: $color-success-dark !important;
      }
    }

    &--pending {
      background-color: $color-primary-lighter !important;
      color: $color-primary-darker !important;
      border-color: $color-primary !important;

      :deep(.p-tag-icon) {
        color: $color-primary-darker !important;
      }
    }
  }
}

:deep(.p-tag) {
  color: $color-primary-darker !important;
  background-color: $color-primary-pink-lighter !important;
}

// Animations
@keyframes expandCollapse {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
